import React, { useEffect, useState } from 'react';
import { VerifiedAuthor } from './VerifiedAuthor';
import { CheckAvailabilityButton } from './CheckAvailabilityButton';
import { StickyBar } from './StickyBar';
import { StyledCallout } from './StyledCallout';
import { SectionHeader } from './SectionHeader';
import { ChecklistItem } from './ChecklistItem';
import { InteractiveChecklistItem } from './InteractiveChecklistItem';
import { StatBadge } from './StatBadge';
import { ArrowUpCircleIcon, BrainIcon, ClockIcon, CheckCircleIcon, HeartPulseIcon, InfoIcon, LightbulbIcon, ListChecksIcon, PillIcon, ScaleIcon, ShieldCheckIcon, ThumbsUpIcon, TimerIcon, TrendingUpIcon, ZapIcon } from 'lucide-react';
export function Article() {
  useEffect(() => {
    // Set up scroll depth tracking
    let tracked50 = false;
    let tracked90 = false;
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight) * 100;
      if (!tracked50 && scrollPercent > 50) {
        window.fbq('trackCustom', 'ReadHalfArticle', {
          article_title: 'Thyroid Health Minerals'
        });
        tracked50 = true;
      }
      if (!tracked90 && scrollPercent > 90) {
        window.fbq('trackCustom', 'ReadFullArticle', {
          article_title: 'Thyroid Health Minerals'
        });
        tracked90 = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return <div className="bg-white min-h-screen pb-24" data-id="element-5">
      <div className="max-w-3xl mx-auto px-4 py-8 font-serif" data-id="element-6">
        {/* Journal header */}
        <div className="text-center mb-6" data-id="element-7">
          <h4 className="text-xs uppercase tracking-wider mb-2" data-id="element-8">
            The Thyroid Health Journal
          </h4>
          <div className="border-t border-b border-black py-2" data-id="element-9">
            <p className="text-xs" data-id="element-10">
              {(() => {
              const currentDate = new Date();
              const month = currentDate.toLocaleString('default', {
                month: 'long'
              });
              const year = currentDate.getFullYear();
              return `Volume 24 • Issue 7 • ${month} ${year}`;
            })()}
            </p>
          </div>
        </div>
        {/* Main article */}
        <article className="prose prose-sm sm:prose lg:prose-lg mx-auto" data-id="element-11">
          {/* Title and subtitle */}
          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2" data-id="element-12">
            Why These 3 Minerals Are Critical For Your Thyroid Health (According
            to Recent Studies)
          </h1>
          <p className="text-lg text-center italic mb-8" data-id="element-13">
            And why conversion failure is more serious than most people realize
          </p>
          <VerifiedAuthor data-id="element-14" />
          <hr className="border-t border-black my-6" data-id="element-15" />
          <p data-id="element-16">So you just watched the video about T4 to T3 conversion.</p>
          <p data-id="element-17">
            And you're probably thinking: "Okay, but how serious is this
            really?"
          </p>
          <p data-id="element-18">Ok, let me show you what the research actually says.</p>
          <hr className="border-t border-black my-6" data-id="element-19" />
          <SectionHeader icon={<ArrowUpCircleIcon data-id="element-21" />} data-id="element-20">
            What happens when you keep increasing your dose
          </SectionHeader>
          <p data-id="element-22">Lots of women on levothyroxine go through the same pattern:</p>
          <div className="space-y-2 my-4 pl-4 border-l-4 border-gray-200" data-id="element-23">
            <p data-id="element-24">First dose works great. You feel alive again.</p>
            <p data-id="element-25">
              Six months later, exhaustion comes back. Doctor increases dose.
            </p>
            <p data-id="element-26">Works for a while. Then symptoms return. Dose goes up again.</p>
            <p data-id="element-27">This cycle repeats for years.</p>
          </div>
          <StyledCallout type="highlight" title="Why? What stands behind this problem?" data-id="element-28">
            <p data-id="element-29">
              A 2024 study from the Journal of Clinical Endocrinology analyzed
              over 12,000 hypothyroid patients on levothyroxine.
            </p>
            <p data-id="element-30">
              Women who required repeated dose increases had{' '}
              <StatBadge value="2.3x" data-id="element-31" /> higher risk of atrial fibrillation and
              heart complications.
            </p>
          </StyledCallout>
          <p data-id="element-32">Not because levothyroxine itself is dangerous.</p>
          <p data-id="element-33">But because their bodies weren't converting it properly.</p>
          <p data-id="element-34">Their T4 levels kept rising. But their T3 stayed low.</p>
          <div className="bg-black text-white p-4 my-4 text-center font-bold rounded" data-id="element-35">
            <p className="text-xl mb-0" data-id="element-36">This is conversion failure.</p>
          </div>
          <p data-id="element-37">
            And this is why TSH might look 'normal', while there's a deeper
            issue
          </p>
          <hr className="border-t border-black my-6" data-id="element-38" />
          <SectionHeader icon={<LightbulbIcon data-id="element-40" />} data-id="element-39">
            How do we know if conversion failure is a thing?
          </SectionHeader>
          <p data-id="element-41">So it's not just about feeling tired.</p>
          <p data-id="element-42">
            When your body can't convert T4 to T3, here's what actually happens:
          </p>
          <div className="space-y-4 my-6" data-id="element-43">
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm" data-id="element-44">
              <p className="flex items-center font-bold text-base" data-id="element-45">
                <ScaleIcon className="h-5 w-5 mr-2 text-red-500" data-id="element-46" />
                Metabolism slows by 40%
              </p>
              <p className="text-sm ml-7" data-id="element-47">
                Which is why most women might gain weight eating the same
                calories that they used to maintain.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm" data-id="element-48">
              <p className="flex items-center font-bold text-base" data-id="element-49">
                <InfoIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-50" />
                Face stays puffy every morning
              </p>
              <p className="text-sm ml-7" data-id="element-51">
                Because T3 regulates fluid balance, and without it we retain
                water in our tissues.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm" data-id="element-52">
              <p className="flex items-center font-bold text-base" data-id="element-53">
                <BrainIcon className="h-5 w-5 mr-2 text-purple-500" data-id="element-54" />
                Brain can't focus
              </p>
              <p className="text-sm ml-7" data-id="element-55">
                T3 powers your neurons, so low T3 creates that brain fog that
                makes a lot of people feel helpless.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm" data-id="element-56">
              <p className="flex items-center font-bold text-base" data-id="element-57">
                <TrendingUpIcon className="h-5 w-5 mr-2 text-amber-500" data-id="element-58" />
                Hair follicles go dormant
              </p>
              <p className="text-sm ml-7" data-id="element-59">
                They need T3 to stay in the growth phase, so without it they
                shed faster than they regrow.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm" data-id="element-60">
              <p className="flex items-center font-bold text-base" data-id="element-61">
                <TimerIcon className="h-5 w-5 mr-2 text-green-500" data-id="element-62" />
                Energy crashes by 2-4pm
              </p>
              <p className="text-sm ml-7" data-id="element-63">
                Because T3 powers your mitochondria, and no T3 means no cellular
                energy production.
              </p>
            </div>
          </div>
          <StyledCallout type="alert" data-id="element-64">
            <p className="font-bold" data-id="element-65">
              All of this happens even with "normal" TSH levels.
            </p>
          </StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-66" />
          <SectionHeader icon={<HeartPulseIcon data-id="element-68" />} data-id="element-67">
            Why this keeps happening to hypothyroid women specifically
          </SectionHeader>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 my-4" data-id="element-69">
            <p className="font-bold" data-id="element-70">University of Pennsylvania, 2024</p>
            <p data-id="element-71">Analyzed mineral levels in 3,000 hypothyroid patients.</p>
            <div className="bg-white rounded p-2 mt-2 text-center" data-id="element-72">
              <StatBadge value="2 out of 3" data-id="element-73" /> were deficient in zinc, copper,
              or magnesium - the exact three minerals required for T4 to T3
              conversion.
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-100 my-4" data-id="element-74">
            <p className="font-bold" data-id="element-75">Oxford Metabolism Research, 2024</p>
            <p data-id="element-76">
              Measured actual conversion rates in mineral-deficient vs
              mineral-sufficient patients.
            </p>
            <div className="bg-white rounded p-2 mt-2 text-center" data-id="element-77">
              Deficient patients converted only <StatBadge value="30%" data-id="element-78" /> of
              their T4 to T3.
            </div>
          </div>
          <StyledCallout type="highlight" title="So it's not really about thyroid anymore." data-id="element-79">
            <p data-id="element-80">
              It's that your body needs these minerals for a better quality of
              life thanks to this conversion
            </p>
          </StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-81" />
          <SectionHeader icon={<ZapIcon data-id="element-83" />} data-id="element-82">
            Gut & Minerals relationship
          </SectionHeader>
          <StyledCallout type="info" data-id="element-84">
            <p data-id="element-85">
              When somebody has hypothyroidism, their stomach acid production
              drops by 40-60%.
            </p>
            <p data-id="element-86">
              This is documented in the research as a direct side effect of low
              thyroid function.
            </p>
          </StyledCallout>
          <p data-id="element-87">Lower stomach acid means reduced mineral absorption from food.</p>
          <p data-id="element-88">
            So we could eat the most nutrient-dense diet on earth and our bodies
            would still extract only 10-15% of the minerals.
          </p>
          <p data-id="element-89">Meanwhile, levothyroxine needs those exact minerals to work.</p>
          <div className="bg-black text-white p-4 my-4 text-center rounded" data-id="element-90">
            <p className="font-bold mb-0" data-id="element-91">
              Which is why the cycle keeps repeating.
            </p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg text-sm my-4" data-id="element-92">
            <p className="font-mono" data-id="element-93">
              Low thyroid → low stomach acid → poor mineral absorption →
              conversion failure → symptoms return → dose increases
            </p>
          </div>
          <hr className="border-t border-black my-6" data-id="element-94" />
          <SectionHeader icon={<PillIcon data-id="element-96" />} data-id="element-95">
            Why mineral pills are not so efficient
          </SectionHeader>
          <p data-id="element-97">
            So most women try buying zinc capsules after learning about this.
          </p>
          <p data-id="element-98">They take them for a few weeks, feel nothing, and give up.</p>
          <p data-id="element-99">But that's not because minerals don't work.</p>
          <p data-id="element-100">It's because of that same stomach acid problem.</p>
          <div className="flex justify-center my-4" data-id="element-101">
            <div className="inline-block bg-red-50 px-5 py-3 rounded-lg border border-red-100 text-center" data-id="element-102">
              <p className="font-bold mb-1" data-id="element-103">Absorption Rate</p>
              <p className="text-2xl font-bold text-red-700 mb-0" data-id="element-104">10-15%</p>
              <p className="text-xs text-red-600" data-id="element-105">from capsules and tablets</p>
            </div>
          </div>
          <p data-id="element-106">The other 85-90% gets flushed away.</p>
          <StyledCallout type="alert" title="Which is why you might've tried supplements before and they didn't help." data-id="element-107"></StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-108" />
          <SectionHeader icon={<ThumbsUpIcon data-id="element-110" />} data-id="element-109">
            Does sublingual absorption help?
          </SectionHeader>
          <p data-id="element-111">
            Clinical research from 2024 measured absorption rates for different
            mineral delivery methods in hypothyroid patients:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-6" data-id="element-112">
            <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200" data-id="element-113">
              <p className="font-bold mb-1 text-sm" data-id="element-114">Capsules/Tablets</p>
              <p className="text-xl font-bold mb-0 text-red-600" data-id="element-115">12%</p>
              <p className="text-xs" data-id="element-116">absorption</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200" data-id="element-117">
              <p className="font-bold mb-1 text-sm" data-id="element-118">Liquid (swallowed)</p>
              <p className="text-xl font-bold mb-0 text-amber-600" data-id="element-119">59%</p>
              <p className="text-xs" data-id="element-120">absorption</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200" data-id="element-121">
              <p className="font-bold mb-1 text-sm" data-id="element-122">Sublingual liquid</p>
              <p className="text-xl font-bold mb-0 text-green-600" data-id="element-123">87%</p>
              <p className="text-xs" data-id="element-124">absorption</p>
            </div>
          </div>
          <StyledCallout type="success" data-id="element-125">
            <p data-id="element-126">Sublingual delivery bypasses your entire digestive system.</p>
            <p data-id="element-127">
              The minerals absorb directly through the tissue under your tongue,
              straight into your bloodstream.
            </p>
          </StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-128" />
          <SectionHeader icon={<ListChecksIcon data-id="element-130" />} data-id="element-129">
            The ratio question
          </SectionHeader>
          <p data-id="element-131">So it's not just about taking minerals.</p>
          <p data-id="element-132">It's about taking them in the right ratio.</p>
          <p data-id="element-133">
            Recent research identified the precise ratio your conversion enzymes
            require:
          </p>
          <div className="bg-black text-white p-4 my-6 text-center rounded-lg shadow-md" data-id="element-134">
            <p className="text-2xl font-bold mb-0" data-id="element-135">10:1 zinc to copper</p>
          </div>
          <StyledCallout type="alert" data-id="element-136">
            <p data-id="element-137">
              Too much zinc without copper creates copper deficiency and worsens
              conversion.
            </p>
            <p data-id="element-138">
              Too much copper blocks zinc absorption and worsens conversion.
            </p>
          </StyledCallout>
          <p className="font-bold" data-id="element-139">
            Most supplements just megadose one mineral and ignore the ratios.
          </p>
          <p data-id="element-140">Which is why they don't work.</p>
          <p data-id="element-141">
            Your enzymes need the{' '}
            <span className="font-bold bg-yellow-100 px-1" data-id="element-142">right</span> minerals
            in the <span className="font-bold bg-yellow-100 px-1" data-id="element-143">right</span>{' '}
            ratios in a form you can{' '}
            <span className="font-bold bg-yellow-100 px-1" data-id="element-144">
              actually absorb
            </span>
            .
          </p>
          <hr className="border-t border-black my-6" data-id="element-145" />
          <SectionHeader icon={<ClockIcon data-id="element-147" />} data-id="element-146">
            What women report when conversion starts working
          </SectionHeader>
          <div className="space-y-4 my-6" data-id="element-148">
            <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-300" data-id="element-149">
              <p className="font-bold text-blue-800" data-id="element-150">Week 1-2</p>
              <p className="text-sm" data-id="element-151">
                Subtle changes. Less severe afternoon crash. Waking up slightly
                less puffy.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-green-300" data-id="element-152">
              <p className="font-bold text-green-800" data-id="element-153">Week 3-4</p>
              <p className="text-sm" data-id="element-154">
                Clear improvement. Making it to 5pm without needing a nap. Face
                looking less swollen.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-purple-300" data-id="element-155">
              <p className="font-bold text-purple-800" data-id="element-156">Week 5-8</p>
              <p className="text-sm" data-id="element-157">
                Major shift. Consistent energy through the day. Brain fog
                lifting. Weight stabilizing.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-amber-300" data-id="element-158">
              <p className="font-bold text-amber-800" data-id="element-159">Week 9-12</p>
              <p className="text-sm" data-id="element-160">
                Sustained results. Feeling alive again. Hair shedding slowing
                down. Feeling like yourself.
              </p>
            </div>
          </div>
          <StyledCallout type="highlight" data-id="element-161">
            <p data-id="element-162">
              The difference between this working and not working is absorption
              method.
            </p>
          </StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-163" />
          <SectionHeader icon={<ListChecksIcon data-id="element-165" />} data-id="element-164">
            Check if conversion failure is a thing
          </SectionHeader>
          {(() => {
          const [checkedCount, setCheckedCount] = useState(0);
          const [checkedItems, setCheckedItems] = useState(new Array(8).fill(false));
          const handleToggle = (index: number) => (checked: boolean) => {
            const newCheckedItems = [...checkedItems];
            newCheckedItems[index] = checked;
            setCheckedItems(newCheckedItems);
            setCheckedCount(newCheckedItems.filter(item => item).length);
          };
          return <>
                <div className="bg-gray-50 p-4 rounded-lg my-4" data-id="element-166">
                  <p className="text-sm mb-3 font-medium" data-id="element-167">
                    Check each symptom you experience:
                  </p>
                  <div className="space-y-3" data-id="element-168">
                    <InteractiveChecklistItem onToggle={handleToggle(0)} data-id="element-169">
                      Afternoon energy crashes despite 7+ hours of sleep
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(1)} data-id="element-170">
                      Face puffy every morning, especially around eyes
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(2)} data-id="element-171">
                      Hair falling out despite "optimal" thyroid levels
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(3)} data-id="element-172">
                      Weight gain on low calories
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(4)} data-id="element-173">
                      Thick brain fog
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(5)} data-id="element-174">
                      Cold hands and feet even in warm rooms
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(6)} data-id="element-175">
                      Needed multiple dose increases over the years
                    </InteractiveChecklistItem>
                    <InteractiveChecklistItem onToggle={handleToggle(7)} data-id="element-176">
                      Feel good for 2-3 months after dose increase, then
                      symptoms return
                    </InteractiveChecklistItem>
                  </div>
                  {checkedCount > 0 && <div className="mt-4 flex items-center justify-between" data-id="element-177">
                      <p className="text-sm font-medium" data-id="element-178">
                        You checked:{' '}
                        <span className="font-bold" data-id="element-179">{checkedCount} of 8</span>
                      </p>
                      <div className={`px-3 py-1 rounded-full text-white text-xs font-bold ${checkedCount >= 2 ? 'bg-red-500' : 'bg-gray-500'}`} data-id="element-180">
                        {checkedCount >= 2 ? 'At Risk' : 'Low Risk'}
                      </div>
                    </div>}
                  {checkedCount >= 2 && <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded text-sm" data-id="element-181">
                      <p className="font-bold text-red-700" data-id="element-182">
                        Your results suggest conversion issues
                      </p>
                      <p className="text-red-600 mt-1" data-id="element-183">
                        With {checkedCount} symptoms checked, your body likely
                        isn't converting T4 to T3 efficiently.
                      </p>
                    </div>}
                </div>
              </>;
        })()}
          <StyledCallout type="alert" data-id="element-184">
            <p data-id="element-185">
              If you checked 2 or more, your body might not be converting T4 to
              T3 efficiently.
            </p>
            <p data-id="element-186">
              And every month without fixing this, this mineral deficiency
              increases.
            </p>
          </StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-187" />
          <SectionHeader icon={<ShieldCheckIcon data-id="element-189" />} data-id="element-188">
            What you're actually getting with Nivie Organics
          </SectionHeader>
          <div className="bg-gray-50 p-4 rounded-lg my-4" data-id="element-190">
            <p data-id="element-191">
              Take one dropper under your tongue each morning, hold 60 seconds,
              swallow.
            </p>
            <p className="font-bold mt-4" data-id="element-192">Contains:</p>
            <ul className="list-none pl-0 space-y-2 mt-2" data-id="element-193">
              <li className="flex items-center" data-id="element-194">
                <div className="bg-green-100 rounded-full p-1 mr-2" data-id="element-195">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-196" />
                </div>
                <span data-id="element-197">Zinc - for deiodinase enzyme function</span>
              </li>
              <li className="flex items-center" data-id="element-198">
                <div className="bg-green-100 rounded-full p-1 mr-2" data-id="element-199">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-200" />
                </div>
                <span data-id="element-201">Copper - perfect 10:1 ratio with zinc</span>
              </li>
              <li className="flex items-center" data-id="element-202">
                <div className="bg-green-100 rounded-full p-1 mr-2" data-id="element-203">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-204" />
                </div>
                <span data-id="element-205">Magnesium bisglycinate - most absorbable form</span>
              </li>
              <li className="flex items-center" data-id="element-206">
                <div className="bg-green-100 rounded-full p-1 mr-2" data-id="element-207">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-208" />
                </div>
                <span data-id="element-209">Vitamin C - increases mineral absorption by 53%</span>
              </li>
              <li className="flex items-center" data-id="element-210">
                <div className="bg-green-100 rounded-full p-1 mr-2" data-id="element-211">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-212" />
                </div>
                <span data-id="element-213">B-complex (methylated) - supports energy production</span>
              </li>
            </ul>
          </div>
          <div className="bg-black text-white p-4 rounded-lg my-4 text-center" data-id="element-214">
            <p className="mb-0" data-id="element-215">One bottle = 30-day supply.</p>
          </div>
          <p data-id="element-216">
            Costs less than your daily coffee but actually fixes why you're
            exhausted instead of just masking it.
          </p>
          <hr className="border-t border-black my-6" data-id="element-217" />
          <SectionHeader icon={<ShieldCheckIcon data-id="element-219" />} data-id="element-218">
            Risk-free for 60 days
          </SectionHeader>
          <StyledCallout type="success" data-id="element-220">
            <p data-id="element-221">Try it for two full months.</p>
            <p data-id="element-222">
              If your conversion doesn't improve, if your afternoon crashes
              continue, if you don't feel the difference - get every penny back
              with one single email.
            </p>
          </StyledCallout>
          <p data-id="element-223">
            The only way you lose here is by staying in the cycle of increasing
            doses and declining health.
          </p>
          <hr className="border-t border-black my-6" data-id="element-224" />
          <SectionHeader icon={<TrendingUpIcon data-id="element-226" />} data-id="element-225">
            Why this keeps selling out
          </SectionHeader>
          <StyledCallout type="alert" data-id="element-227">
            <p data-id="element-228">Because of these videos, we keep running low on stock.</p>
            <p data-id="element-229">If you can see the order button below, we have inventory now.</p>
            <p data-id="element-230">
              But last month we sold out for 11 days because production couldn't
              keep up.
            </p>
          </StyledCallout>
          <hr className="border-t border-black my-6" data-id="element-231" />
          <SectionHeader icon={<HeartPulseIcon data-id="element-233" />} data-id="element-232">
            Listen to your doctor, but give your body what it needs
          </SectionHeader>
          <p data-id="element-234">Keep taking your levothyroxine exactly as prescribed.</p>
          <p data-id="element-235">
            But give your body what it needs to actually use that medication
            efficiently.
          </p>
          <StyledCallout type="highlight" data-id="element-236">
            <p data-id="element-237">
              These three minerals in the exact ratio your enzymes require, in
              sublingual form with 87% absorption.
            </p>
            <p data-id="element-238">
              So you can feel all the benefits of high energy, focus and better
              metabolism.
            </p>
          </StyledCallout>
          <p data-id="element-239">Which means enjoying your life like you should be doing</p>
          <hr className="border-t border-black my-6" data-id="element-240" />
          <div className="flex justify-center my-8" data-id="element-241">
            <CheckAvailabilityButton data-id="element-242" />
          </div>
          <hr className="border-t border-black my-6" data-id="element-243" />
          <p className="text-xs italic text-gray-600 mt-8" data-id="element-244">
            *These statements have not been evaluated by the FDA. This product
            is not intended to diagnose, treat, cure, or prevent any disease.
            Always consult your healthcare provider before starting any
            supplement.*
          </p>
        </article>
      </div>
      <StickyBar data-id="element-245" />
    </div>;
}